import { useMemo, useState } from "react";
import {
  useGetMyCyclesQuery,
  useGetMyCycleDetailQuery,
  useGetMyStatusQuery,
} from "@/store/api";
import type { MyCycleRow } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/dialog";
import { HourlyOpenPositions } from "@/components/domain/HourlyOpenPositions";
import { fmtUsd, fmtTime, fmtRelativeFuture } from "@/lib/format";

const STATUSES = ["all", "executed", "queued", "held", "skipped", "error"] as const;
type StatusFilter = (typeof STATUSES)[number];

export default function MyCycles() {
  const { data, isLoading } = useGetMyCyclesQuery(100, { pollingInterval: 30000 });
  const status = useGetMyStatusQuery(undefined, { pollingInterval: 30000 });
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [activeId, setActiveId] = useState<number | null>(null);

  const rows = useMemo<MyCycleRow[]>(() => {
    if (!data) return [];
    if (filter === "all") return data;
    return data.filter((r) => r.status === filter);
  }, [data, filter]);

  const nextCycleAt = status.data?.next_cycle_at;

  return (
    <div className="space-y-4">
      {nextCycleAt && (
        <p className="text-xs text-muted">
          Next cycle runs at{" "}
          <span className="text-fg tabular">{fmtTime(nextCycleAt)}</span>{" "}
          ({fmtRelativeFuture(nextCycleAt)}).
        </p>
      )}

      <HourlyOpenPositions pollMs={15000} />

      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle>Your cycle history</CardTitle>
            <p className="text-xs text-muted mt-1">
              One row per hourly cycle. The bot's reasoning is shared
              across all users — what differs is how it applied to your
              account (your caps, balance, and approval mode).
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={
                  "px-2.5 h-7 rounded-md text-xs border transition-colors capitalize " +
                  (filter === s
                    ? "bg-bg text-fg border-border"
                    : "text-muted border-transparent hover:text-fg hover:bg-bg")
                }
              >
                {s}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted">
              No cycles yet. The bot runs hourly — once it does, you'll
              see what it decided and how it applied to your account.
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>When</TH>
                  <TH>Action</TH>
                  <TH>Symbol</TH>
                  <TH className="text-right">Proposed</TH>
                  <TH className="text-right">Applied</TH>
                  <TH>Status</TH>
                  <TH>Notes</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((r) => (
                  <TR
                    key={r.id}
                    onClick={() => setActiveId(r.id)}
                    className="cursor-pointer"
                  >
                    <TD className="text-xs text-muted">
                      {fmtTime(r.cycle_started_at)}
                    </TD>
                    <TD>
                      {r.action ? (
                        <Badge
                          tone={
                            r.action === "BUY" ? "good"
                            : r.action === "SELL" ? "bad"
                            : "muted"
                          }
                        >
                          {r.action}
                        </Badge>
                      ) : "—"}
                    </TD>
                    <TD className="font-medium">{r.symbol ?? "—"}</TD>
                    <TD className="text-right tabular text-muted">
                      {r.proposed_size_usd != null ? fmtUsd(r.proposed_size_usd) : "—"}
                    </TD>
                    <TD className="text-right tabular">
                      {r.applied_size_usd != null ? fmtUsd(r.applied_size_usd) : "—"}
                    </TD>
                    <TD>
                      <StatusBadge status={r.status} />
                    </TD>
                    <TD className="text-xs text-muted truncate max-w-xs">
                      {r.notes ?? r.error ?? ""}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <MyCycleDetailDialog
        ucId={activeId}
        onClose={() => setActiveId(null)}
      />
    </div>
  );
}

// Friendlier labels for the step trace shown to users.
const STEP_LABELS: Record<string, string> = {
  macro_context: "Macro context",
  market_analysis: "Market analysis",
  draft_trade: "Draft trade",
  final_decision: "Final decision",
  deep_analysis: "Deep advisor (second opinion)",
  risk_guard: "Risk guard",
};

function labelFor(name: string): string {
  return STEP_LABELS[name] ?? name;
}

function parseStepReasoning(detailJson: string | null | undefined): string {
  if (!detailJson) return "";
  try {
    const obj = JSON.parse(detailJson);
    // Common shapes: {reasoning: "..."} (decisions), {text: "..."} (raw LLM),
    // or just a string.
    if (typeof obj === "string") return obj;
    if (typeof obj?.reasoning === "string") return obj.reasoning;
    if (typeof obj?.text === "string") {
      // The text field is sometimes a fenced JSON block — try to pull
      // `reasoning` out of it if so, otherwise return as-is.
      const cleaned = obj.text.replace(/```(?:json)?\s*|\s*```$/g, "").trim();
      try {
        const inner = JSON.parse(cleaned);
        if (typeof inner?.reasoning === "string") return inner.reasoning;
      } catch { /* fallthrough */ }
      return obj.text;
    }
    return "";
  } catch {
    return "";
  }
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "executed" ? "good"
    : status === "queued" ? "info"
    : status === "held" ? "muted"
    : status === "error" ? "bad"
    : "warn";
  return <Badge tone={tone}>{status}</Badge>;
}

function MyCycleDetailDialog({
  ucId,
  onClose,
}: {
  ucId: number | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetMyCycleDetailQuery(ucId ?? 0, {
    skip: ucId == null,
  });
  return (
    <Dialog open={ucId != null} onClose={onClose} title="Cycle details" size="lg">
      {isLoading || !data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-muted uppercase">When</div>
                <div>{fmtTime(data.row.cycle_started_at)}</div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase">Status</div>
                <StatusBadge status={data.row.status} />
              </div>
              <div>
                <div className="text-xs text-muted uppercase">Action</div>
                <div>{data.row.action ?? "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase">Symbol</div>
                <div>{data.row.symbol ?? "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase">Proposed size</div>
                <div className="tabular">
                  {data.row.proposed_size_usd != null
                    ? fmtUsd(data.row.proposed_size_usd) : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase">Applied to you</div>
                <div className="tabular">
                  {data.row.applied_size_usd != null
                    ? fmtUsd(data.row.applied_size_usd) : "—"}
                </div>
              </div>
              {data.row.pending_id && (
                <div className="col-span-2">
                  <div className="text-xs text-muted uppercase">Pending</div>
                  <div className="font-mono text-xs">{data.row.pending_id}</div>
                </div>
              )}
              {data.row.trade_order_id && (
                <div className="col-span-2">
                  <div className="text-xs text-muted uppercase">Order id</div>
                  <div className="font-mono text-xs">{data.row.trade_order_id}</div>
                </div>
              )}
            </div>
            {(data.row.notes || data.row.error) && (
              <div>
                <div className="text-xs text-muted uppercase mb-1">Notes</div>
                <div className="text-sm bg-bg2 rounded p-2 whitespace-pre-wrap">
                  {data.row.notes ?? data.row.error}
                </div>
              </div>
            )}
            {data.row.personal_review_reasoning && (
              <div>
                <div className="text-xs text-muted uppercase mb-1">
                  Personal AI review
                  {data.row.personal_review_action && (
                    <span className="ml-2 normal-case">
                      → {data.row.personal_review_action}
                    </span>
                  )}
                  {data.row.personal_review_model && (
                    <span className="ml-2 normal-case text-muted/70">
                      ({data.row.personal_review_model})
                    </span>
                  )}
                </div>
                <div className="text-sm bg-bg2 rounded p-2 whitespace-pre-wrap">
                  {data.row.personal_review_reasoning}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-muted uppercase mb-2">
                Bot's analysis this cycle
              </div>
              {data.steps.length === 0 ? (
                <p className="text-xs text-muted">No step trace recorded.</p>
              ) : (
                <ol className="space-y-2 text-xs">
                  {data.steps
                    // Hide steps that leak admin-side data or just restate
                    // the status pill above (snapshot = admin's balance;
                    // outcome = "HOLD: no trade" duplicates the badge).
                    .filter((s) => s.name !== "snapshot" && s.name !== "outcome")
                    .map((s) => {
                      const fullReasoning = parseStepReasoning(s.detail_json);
                      return (
                        <li
                          key={s.id}
                          className="rounded-md border border-border p-2 space-y-1"
                        >
                          <div className="flex gap-2 items-baseline">
                            <span className="font-medium">{labelFor(s.name)}</span>
                            <span
                              className={
                                s.status === "ok" ? "text-good"
                                : s.status === "skip" ? "text-muted"
                                : "text-bad"
                              }
                            >
                              {s.status}
                            </span>
                          </div>
                          <p className="text-xs text-muted whitespace-pre-wrap">
                            {fullReasoning || s.summary || ""}
                          </p>
                        </li>
                      );
                    })}
                </ol>
              )}
            </div>
        </div>
      )}
    </Dialog>
  );
}
